import { useRef, useState } from "react";
import { fetchJson } from "../lib/fetchJson";
import { INITIAL_REPORT_FILTERS } from "../lib/constants";

export function useReports({ currentUser, roleScope }) {
  const [reportData, setReportData] = useState(null);
  const [activeReportTab, setActiveReportTab] = useState("Funnel");
  const [reportFilters, setReportFilters] = useState(INITIAL_REPORT_FILTERS);
  const [reportDraftFilters, setReportDraftFilters] = useState(INITIAL_REPORT_FILTERS);
  const [reportSyncKey, setReportSyncKey] = useState(0);
  const [loadingReports, setLoadingReports] = useState(false);
  const [exportingReport, setExportingReport] = useState(false);

  const reportReqSeqRef = useRef(0);

  async function loadReports(user, options = {}) {
    if (!user) return;

    if (!options.silent) {
      setLoadingReports(true);
    }

    try {
      const params = new URLSearchParams({
        period: reportFilters.period,
      });

      if (reportFilters.dateFrom) {
        params.set("date_from", reportFilters.dateFrom);
      }
      if (reportFilters.dateTo) {
        params.set("date_to", reportFilters.dateTo);
      }
      if (reportFilters.sourceId) {
        params.set("source", reportFilters.sourceId);
      }
      if (reportFilters.region) {
        params.set("region", reportFilters.region);
      }
      if (reportFilters.teamId) {
        params.set("team", reportFilters.teamId);
      }
      if (reportFilters.status) {
        params.set("status", reportFilters.status);
      }
      if (roleScope?.scope === "own_leads") {
        params.set("assigned_to", String(user.id));
      } else if (reportFilters.assignedToId) {
        params.set("assigned_to", reportFilters.assignedToId);
      }
      // Dashboard always shows B2C leads only
      if (options.b2cOnly) {
        params.set("campaign_type", "b2c");
      }

      // Stale-response guard
      const seq = ++reportReqSeqRef.current;
      const payload = await fetchJson(`/api/crm/reports/funnel/?${params.toString()}`);
      if (seq === reportReqSeqRef.current) {
        setReportData(payload);
      }
      return payload;
    } finally {
      if (!options.silent) {
        setLoadingReports(false);
      }
    }
  }

  function updateReportDraftFilter(field, value) {
    const apply = (current) => {
      const next = { ...current, [field]: value };
      if (field === "period") {
        next.dateFrom = "";
        next.dateTo = "";
      }
      return next;
    };
    setReportDraftFilters(apply);
    setReportFilters(apply);
  }

  function applyReportFilters(patch) {
    const next = { ...reportDraftFilters, ...patch };
    setReportDraftFilters(next);
    setReportFilters(next);
    setReportSyncKey((k) => k + 1);
  }

  function resetReportFilters() {
    setReportDraftFilters(INITIAL_REPORT_FILTERS);
    setReportFilters(INITIAL_REPORT_FILTERS);
  }

  function resetReportFiltersTodayDefault() {
    const todayStr = new Date().toISOString().slice(0, 10);
    const todayFilters = { ...INITIAL_REPORT_FILTERS, dateFrom: todayStr, dateTo: todayStr };
    setReportDraftFilters(todayFilters);
    setReportFilters(todayFilters);
    setReportSyncKey((k) => k + 1);
  }

  function triggerReportSync() {
    setReportSyncKey((k) => k + 1);
  }

  function resetReports() {
    setReportData(null);
    setActiveReportTab("Funnel");
    setReportFilters(INITIAL_REPORT_FILTERS);
    setReportDraftFilters(INITIAL_REPORT_FILTERS);
  }

  return {
    activeReportTab,
    applyReportFilters,
    exportingReport,
    loadReports,
    loadingReports,
    reportData,
    reportDraftFilters,
    reportFilters,
    reportSyncKey,
    resetReportFilters,
    resetReportFiltersTodayDefault,
    resetReports,
    setActiveReportTab,
    setExportingReport,
    setLoadingReports,
    setReportData,
    setReportDraftFilters,
    setReportFilters,
    triggerReportSync,
    updateReportDraftFilter,
  };
}
